import { useEffect, useRef, useState } from "react";
import { database } from "../services/firebase";
import {
  collection,
  collectionGroup,
  doc,
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  startAfter,
  startAt,
  where
} from "firebase/firestore";

export function useListener(props, onListenerUpdated) {
  log(props.enableLogs, "useListener props:", props);
  const listenerRef = useRef({});
  const [loading, setLoading] = useState({
    pagination: false,
    search: false
  });
  const dataStore = useRef({});
  const currentCursor = useRef({});
  const filterQuery = useRef([]);
  const listeningFilterData = useRef([]);
  const dataLimit = props.dataLimit;
  const pageCursors = useRef({});
  const currentPageIndex = useRef({});

  let docIds = [];
  let isCollectionLevel = false;

  // A single document under a top-level collection (no sub-collection) is
  // addressed directly, e.g. config/featureFlags — not queried as a list.
  const isDocumentLevel = Boolean(
    props.collectionName &&
      !props.subCollectionName &&
      props.collectionDocId &&
      !Array.isArray(props.collectionDocId)
  );

  if (Array.isArray(props.collectionDocId)) {
    docIds = props.collectionDocId.filter((id) => id != null);
  } else if (props.collectionDocId) {
    docIds = [props.collectionDocId];
  } else if (
    (props.collectionName && !props.subCollectionName) ||
    (!props.collectionName && props.subCollectionName)
  ) {
    isCollectionLevel = true;
    docIds = ["__collection__"];
  } else {
    docIds = [];
  }

  const generateQueryRef = (docId, isNextPage) => {
    try {
      log(
        props.enableLogs,
        `Generating query for docId: ${docId}, isNextPage: ${isNextPage}`
      );

      if (isDocumentLevel && docId) {
        log(
          props.enableLogs,
          `Document ref: ${props.collectionName}/${docId}`
        );
        return doc(database, props.collectionName, docId);
      }

      let baseQuery;

      if (props.collectionName && !props.subCollectionName) {
        baseQuery = collection(database, props.collectionName);
        log(props.enableLogs, `Collection query: ${props.collectionName}`);
      }

      if (props.subCollectionName && !props.collectionName) {
        baseQuery = collectionGroup(database, props.subCollectionName);
        log(
          props.enableLogs,
          `Collection group query: ${props.subCollectionName}`
        );
      }

      if (
        props.subCollectionName &&
        props.collectionName &&
        docId &&
        !isCollectionLevel
      ) {
        baseQuery = collection(
          database,
          props.collectionName,
          docId,
          props.subCollectionName
        );
        log(
          props.enableLogs,
          `Sub-collection query: ${props.collectionName}/${docId}/${props.subCollectionName}`
        );
      }

      if (filterQuery.current && filterQuery.current.length > 0) {
        log(props.enableLogs, `Applying filters:`, filterQuery.current);

        for (let i = 0; i < filterQuery.current.length; i += 3) {
          const field = filterQuery.current[i];
          const operator = filterQuery.current[i + 1];
          const value = filterQuery.current[i + 2];

          if (field && operator && typeof value !== "undefined") {
            log(props.enableLogs, `  Filter: ${field} ${operator}`, value);
            baseQuery = query(baseQuery, where(field, operator, value));
          } else {
            log(props.enableLogs, `  Invalid filter segment skipped:`, {
              field,
              operator,
              value,
              index: i
            });
          }
        }
      }

      if (props.orderBy) {
        log(
          props.enableLogs,
          `Order by: ${props.orderBy} ${props.sortBy || "desc"}`
        );
        baseQuery = query(
          baseQuery,
          orderBy(props.orderBy, props.sortBy || "desc")
        );
      }

      if (isNextPage && currentCursor.current[docId]) {
        log(props.enableLogs, `Adding pagination cursor for docId: ${docId}`);
        baseQuery = query(baseQuery, startAfter(currentCursor.current[docId]));
      }

      log(props.enableLogs, `Query generated successfully for docId: ${docId}`);
      return baseQuery;
    } catch (error) {
      log(props.enableLogs, `Error generating query reference:`, error);
      console.error(
        `Error generating query reference from ${props.collectionName} ${props.subCollectionName} for docId ${docId}:`,
        error
      );
      throw error;
    }
  };

  const getNextPage = async (docId, preLoadedLimit, latestData) => {
    if (isDocumentLevel) {
      log(props.enableLogs, `Pagination is not applicable for a single document: ${docId}`);
      return;
    }

    log(
      props.enableLogs,
      `Getting next page for docId: ${docId}, limit: ${
        preLoadedLimit || dataLimit
      }`
    );
    setLoading((prev) => ({ ...prev, pagination: true }));

    try {
      if (!currentCursor.current[docId]) {
        log(
          props.enableLogs,
          `No cursor available for docId: ${docId}, stopping pagination`
        );
        setLoading((prev) => ({ ...prev, pagination: false }));
        return;
      }

      let nextQuery = generateQueryRef(docId, true);

      if (dataLimit) {
        nextQuery = query(nextQuery, limit(preLoadedLimit ?? dataLimit));
      }

      log(props.enableLogs, `Fetching next page data for docId: ${docId}`);
      const snapshot = await getDocs(nextQuery);

      const result = {};
      snapshot.forEach((doc) => {
        result[doc.id] = { ...doc.data(), documentId: doc.id };
      });

      log(
        props.enableLogs,
        `Received ${Object.keys(result).length} documents for docId: ${docId}`
      );

      if (!dataStore.current[docId]) {
        dataStore.current[docId] = {};
      }

      dataStore.current[docId] = latestData
        ? { ...latestData, ...result }
        : { ...dataStore.current[docId], ...result };

      currentCursor.current[docId] = snapshot.docs[snapshot.docs.length - 1];

      log(
        props.enableLogs,
        `Next page loaded successfully for docId: ${docId}, total documents: ${
          Object.keys(dataStore.current[docId]).length
        }`
      );

      if (typeof onListenerUpdated === "function") {
        onListenerUpdated({
          type: `${props.key?.toUpperCase()}_UPDATED`,
          payload: isCollectionLevel
            ? dataStore.current[docId]
            : dataStore.current,
          docId: isCollectionLevel ? undefined : docId,
          isListenerRunning: true
        });
      }
    } catch (error) {
      log(
        props.enableLogs,
        `Error getting next page for docId: ${docId}:`,
        error
      );
      console.error("Error getting next page:", error);
    } finally {
      setLoading((prev) => ({ ...prev, pagination: false }));
    }
  };

  const getPreviousPage = async (docId) => {
    if (isDocumentLevel) {
      log(props.enableLogs, `Pagination is not applicable for a single document: ${docId}`);
      return;
    }

    if (currentPageIndex.current[docId] <= 0) {
      console.log("Already at first page for docId:", docId);
      return;
    }

    setLoading((prev) => ({ ...prev, pagination: true }));

    try {
      const previousCursor =
        pageCursors.current[docId]?.[currentPageIndex.current[docId] - 1];

      let prevQuery = query(generateQueryRef(docId), startAt(previousCursor));

      if (dataLimit) {
        prevQuery = query(prevQuery, limit(dataLimit));
      }

      const snapshot = await getDocs(prevQuery);
      if (snapshot.empty) {
        console.log("No previous page data for docId:", docId);
        return;
      }

      const result = {};
      snapshot.forEach((doc) => {
        result[doc.id] = { ...doc.data(), documentId: doc.id };
      });

      currentPageIndex.current[docId]--;
      currentCursor.current[docId] = snapshot.docs[snapshot.docs.length - 1];

      dataStore.current[docId] = result;

      if (typeof onListenerUpdated === "function") {
        onListenerUpdated({
          type: `${props.key?.toUpperCase()}_UPDATED`,
          payload: isCollectionLevel
            ? dataStore.current[docId]
            : dataStore.current,
          docId: isCollectionLevel ? undefined : docId,
          isListenerRunning: true
        });
      }
    } catch (error) {
      console.error("Error getting previous page:", error);
      if (typeof onListenerUpdated === "function") {
        onListenerUpdated({
          type: "ERROR",
          payload: error,
          docId: isCollectionLevel ? undefined : docId,
          isListenerRunning: true
        });
      }
    } finally {
      setLoading((prev) => ({ ...prev, pagination: false }));
    }
  };

  const getDocCount = async (docId, queryRef) => {
    try {
      const countSnap = await getCountFromServer(queryRef);
      const count = countSnap.data().count;

      if (typeof onListenerUpdated === "function") {
        onListenerUpdated({
          type: `${props.key?.toUpperCase()}_COUNT_UPDATED`,
          payload: count,
          docId: isCollectionLevel ? undefined : docId,
          isListenerRunning: true
        });
      }
    } catch (error) {
      console.error("Error getting document count:", error);
    }
  };

  const subscribe = (docId) => {
    log(props.enableLogs, `Starting subscription for docId: ${docId}`);

    if (isDocumentLevel) {
      try {
        const docRef = generateQueryRef(docId);

        return onSnapshot(
          docRef,
          (snapshot) => {
            log(
              props.enableLogs,
              `Received document snapshot for docId: ${docId}, exists: ${snapshot.exists()}`
            );

            dataStore.current[docId] = snapshot.exists()
              ? { ...snapshot.data(), documentId: snapshot.id }
              : null;

            if (typeof onListenerUpdated === "function") {
              onListenerUpdated({
                type: `${props.key?.toUpperCase()}_UPDATED`,
                payload: dataStore.current,
                docId,
                isListenerRunning: true
              });
            }
          },
          (error) => {
            log(
              props.enableLogs,
              `Firestore listener error for docId: ${docId}:`,
              error
            );
            console.error(
              `Firestore listener error from ${props.key}: ${props.collectionName}/${docId}`,
              error
            );

            if (typeof onListenerUpdated === "function") {
              onListenerUpdated({
                type: "ERROR",
                payload: error,
                docId,
                isListenerRunning: false
              });
            }
          }
        );
      } catch (error) {
        log(
          props.enableLogs,
          `Error creating subscription for docId: ${docId}:`,
          error
        );
        console.error("Error creating subscription:", error);
        return;
      }
    }

    try {
      let baseQuery = generateQueryRef(docId);

      if (dataLimit) {
        baseQuery = query(baseQuery, limit(dataLimit));
        log(
          props.enableLogs,
          `Applied data limit: ${dataLimit} for docId: ${docId}`
        );
      }

      return onSnapshot(
        baseQuery,
        (snapshot) => {
          log(
            props.enableLogs,
            `Received snapshot update for docId: ${docId}, size: ${snapshot.size}`
          );

          const liveData = {};
          snapshot.forEach((doc) => {
            liveData[doc.id] = { ...doc.data(), documentId: doc.id };
          });

          log(
            props.enableLogs,
            `Processed ${
              Object.keys(liveData).length
            } live documents for docId: ${docId}`
          );

          currentCursor.current[docId] = snapshot.docs.length
            ? snapshot.docs[snapshot.docs.length - 1]
            : null;

          if (
            dataStore.current[docId] &&
            Object.values(dataStore.current[docId])?.length > dataLimit
          ) {
            const previousCount =
              Object.values(dataStore.current[docId])?.length - dataLimit;

            log(
              props.enableLogs,
              `Auto-loading next page with previous count: ${previousCount} for docId: ${docId}`
            );
            getNextPage(docId, previousCount, liveData);
          }

          dataStore.current[docId] = liveData;

          getDocCount(docId, baseQuery);

          log(
            props.enableLogs,
            `Triggering listener update for docId: ${docId}`
          );

          if (typeof onListenerUpdated === "function") {
            onListenerUpdated({
              type: `${props.key?.toUpperCase()}_UPDATED`,
              payload: isCollectionLevel ? liveData : dataStore.current,
              docId: isCollectionLevel ? undefined : docId,
              isListenerRunning: true
            });
          }
        },
        (error) => {
          log(
            props.enableLogs,
            `Firestore listener error for docId: ${docId}:`,
            error
          );
          console.error(
            `Firestore listener error from ${props.key}: ${props.collectionName} ${props.subCollectionName}`,
            error
          );

          if (typeof onListenerUpdated === "function") {
            onListenerUpdated({
              type: "ERROR",
              payload: error,
              docId: isCollectionLevel ? undefined : docId,
              isListenerRunning: false
            });
          }
        }
      );
    } catch (error) {
      log(
        props.enableLogs,
        `Error creating subscription for docId: ${docId}:`,
        error
      );
      console.error("Error creating subscription:", error);
    }
  };

  useEffect(() => {
    docIds.forEach((docId) => {
      if (!currentCursor.current[docId]) {
        currentCursor.current[docId] = null;
      }
      if (!currentPageIndex.current[docId]) {
        currentPageIndex.current[docId] = 0;
      }
      if (!pageCursors.current[docId]) {
        pageCursors.current[docId] = [];
      }
      if (!dataStore.current[docId]) {
        dataStore.current[docId] = {};
      }
    });
    // eslint-disable-next-line
  }, [docIds.length]);

  useEffect(() => {
    if (!props.isListening) {
      log(props.enableLogs, `Listening disabled, skipping subscription setup`);
      return;
    }

    log(props.enableLogs, `Setting up listeners for docIds:`, docIds);

    Object.keys(listenerRef.current).forEach((docId) => {
      if (typeof listenerRef.current[docId] === "function") {
        log(
          props.enableLogs,
          `Unsubscribing existing listener for docId: ${docId}`
        );
        listenerRef.current[docId]();
      }
    });

    docIds.forEach((docId) => {
      log(props.enableLogs, `Subscribing to docId: ${docId}`);
      listenerRef.current[docId] = subscribe(docId);
    });

    Object.keys(listenerRef.current).forEach((docId) => {
      if (
        !docIds.includes(docId) &&
        typeof listenerRef.current[docId] === "function"
      ) {
        log(props.enableLogs, `Cleaning up old listener for docId: ${docId}`);
        listenerRef.current[docId]();
        delete listenerRef.current[docId];
      }
    });
    // eslint-disable-next-line
  }, [props.sortBy, docIds.length, props.isListening]);

  useEffect(() => {
    const newFilterQuery = Array.isArray(props.filterQuery)
      ? props.filterQuery
      : [];
    filterQuery.current = newFilterQuery;
    log(props.enableLogs, `Filter query updated:`, filterQuery.current);

    if (
      props.isListening &&
      JSON.stringify(listeningFilterData.current) !==
        JSON.stringify(filterQuery.current)
    ) {
      log(props.enableLogs, `Filters changed, resetting listeners and data`);

      dataStore.current = {};
      currentCursor.current = {};
      currentPageIndex.current = {};

      listeningFilterData.current = [...filterQuery.current];

      Object.keys(listenerRef.current).forEach((docId) => {
        if (typeof listenerRef.current[docId] === "function") {
          log(
            props.enableLogs,
            `Unsubscribing for filter change, docId: ${docId}`
          );
          listenerRef.current[docId]();
        }
      });

      docIds.forEach((docId) => {
        currentCursor.current[docId] = null;
        currentPageIndex.current[docId] = 0;
        pageCursors.current[docId] = [];
        dataStore.current[docId] = {};
      });

      docIds.forEach((docId) => {
        log(
          props.enableLogs,
          `Re-subscribing after filter change, docId: ${docId}`
        );
        listenerRef.current[docId] = subscribe(docId);
      });
    }
    // eslint-disable-next-line
  }, [props.filterQuery, props.orderBy, docIds.length, props.isListening]);

  useEffect(() => {
    if (props.isListening && Object.keys(listenerRef.current).length === 0) {
      docIds.forEach((docId) => {
        listenerRef.current[docId] = subscribe(docId);
      });

      onListenerUpdated({
        isListenerRunning: true
      });
    } else if (!props.isListening) {
      Object.keys(listenerRef.current).forEach((docId) => {
        if (typeof listenerRef.current[docId] === "function") {
          listenerRef.current[docId]();
        }
      });
      listenerRef.current = {};
      dataStore.current = {};
      currentCursor.current = {};
      currentPageIndex.current = {};
      pageCursors.current = {};

      onListenerUpdated({
        isListenerRunning: false
      });
    }

    // eslint-disable-next-line
  }, [props.isListening, docIds.length]);

  useEffect(() => {
    return () => {
      Object.keys(listenerRef.current).forEach((docId) => {
        if (typeof listenerRef.current[docId] === "function") {
          listenerRef.current[docId]();
        }
      });
    };
  }, []);

  if (isCollectionLevel) {
    const docId = "__collection__";
    return [
      (preLoadedLimit, latestData) =>
        getNextPage(docId, preLoadedLimit, latestData),
      () => getPreviousPage(docId),
      currentPageIndex.current[docId] || 0,
      loading
    ];
  }

  if (docIds.length === 1) {
    const docId = docIds[0];
    return [
      (preLoadedLimit, latestData) =>
        getNextPage(docId, preLoadedLimit, latestData),
      () => getPreviousPage(docId),
      currentPageIndex.current[docId] || 0,
      loading
    ];
  }

  const result = {};
  docIds.forEach((docId) => {
    result[docId] = {
      getNextPage: (preLoadedLimit, latestData) =>
        getNextPage(docId, preLoadedLimit, latestData),
      getPreviousPage: () => getPreviousPage(docId),
      currentPageIndex: currentPageIndex.current[docId] || 0,
      loading,
      data: dataStore.current[docId] || {}
    };
  });

  return result;
}

const log = (enableLogs, message, ...args) => {
  if (enableLogs) {
    console.log(`[useListener]`, message, ...args);
  }
};
